import { FC, useMemo, useRef, useState } from "react";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterModel,
  GridLoadingOverlay,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { Alert, Box, MenuItem, Paper, Select } from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { useFetchEmployeesRecords } from "src/Pages/Company/Absences/api/useEmployee";
import { useTableOperators } from "src/utils/hooks/absences/useTableOperators";
import { useGridStatePersistence } from "src/utils/hooks/absences/useGridStatePersistence";
import parseTableFilters from "src/utils/Company/Absences/parseTableFilters";
import formatEmptyValue from "src/utils/Company/Absences/formatEmptyValue";
import Avatar from "src/Components/Avatar";
import GridLinkAction from "src/Pages/Company/Absences/Tables/GridLinkAction";
import CustomToolbar from "src/Pages/Company/Absences/Tables/CustomToolbar";
import {
  EmploymentForm,
  getEmploymentFormLabels,
  getEmploymentFormOptions,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";
import { TableSettings } from "src/Pages/Company/Absences/Tables/tables.models";
import { IEmployeeRecord } from "./AbsenceRecords.models";

const STORAGE_KEY = "absence-records-employees-table-state";

const AbsenceRecordsEmployeesTable: FC = () => {
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const apiRef = useGridApiRef();
  const currentYear = new Date().getFullYear().toString();

  const { getStringOperators } = useTableOperators();

  const { initialState } = useGridStatePersistence({
    storageKey: STORAGE_KEY,
    apiRef,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [
      {
        field: "active",
        operator: "equals",
        value: "true",
      },
    ],
    logicOperator: GridLogicOperator.And,
  });

  const [settings, setSettings] = useState<TableSettings>({
    density: "standard",
  });

  const [showFilters, setShowFilters] = useState(true);

  const requestParams = useMemo(
    () => ({
      companyId,
      currentYear,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      sort: sortModel?.map(({ field, sort }) => `${field},${sort}`),
      filters: parseTableFilters(filterModel),
    }),
    [
      companyId,
      currentYear,
      filterModel,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
    ]
  );

  const { data, isFetching, isError, error } =
    useFetchEmployeesRecords(requestParams);

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const employmentFormLabels = useMemo(() => getEmploymentFormLabels(), []);
  const employmentFormOptions = useMemo(() => getEmploymentFormOptions(), []);

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          headerName: __("Pracownik"),
          field: "employee",
          type: "string",
          width: 80,
          renderCell: (params) => (
            <Avatar
              initials={params.row.initials}
              src={params.row.avatarUrl}
              employeePresence={params.row.presenceStatus}
            />
          ),
          filterable: false,
          sortable: false,
        },
        {
          field: "employeeFirstName",
          headerName: __("Imię"),
          width: 200,
          type: "string",
          valueGetter: (_value, row) => row.firstName,
          filterOperators: getStringOperators({ labelUT: "Podaj imię" }),
        },
        {
          field: "employeeLastName",
          headerName: __("Nazwisko"),
          width: 200,
          valueGetter: (_value, row) => row.lastName,
          filterOperators: getStringOperators({ labelUT: "Podaj nazwisko" }),
        },
        {
          field: "employeeFkNumber",
          headerName: __("Numer kadrowy"),
          width: 200,
          valueGetter: (_value, row) => row.fkNumber,
          filterOperators: getStringOperators({
            labelUT: "Podaj numer kadrowy",
          }),
        },
        {
          field: "calendarName",
          headerName: __("Aktualny kalendarz"),
          width: 350,
          filterOperators: getStringOperators({
            labelUT: "Podaj nazwę kalendarza",
          }),
          valueGetter: (_value, row) => row.calendar?.name,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Stanowisko"),
          field: "position",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj stanowisko" }),
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Wyświetlany dodatkowy obszar (dział)"),
          field: "additionalDisplayArea",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj obszar" }),
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Obszar"),
          field: "area",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj obszar" }),
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Forma zatrudnienia"),
          field: "employmentType",
          type: "singleSelect",
          width: 200,
          valueGetter: (_value, row) => {
            const { employmentType } = row;
            return employmentType &&
              Object.values(EmploymentForm).includes(
                employmentType as EmploymentForm
              )
              ? employmentType
              : null;
          },
          valueFormatter: (value) => {
            if (value === null) return __("-");
            return value in EmploymentForm
              ? employmentFormLabels[value as EmploymentForm]
              : __("Nieznana forma zatrudnienia");
          },
          filterOperators: [
            {
              label: __("równe"),
              value: "equals",
              getApplyFilterFn: (filterItem) => (params) =>
                filterItem.value === "" || params.value === filterItem.value,
              InputComponent: ({
                item,
                applyValue,
              }: GridFilterInputValueProps) => (
                <Select
                  value={item.value}
                  onChange={(e) =>
                    applyValue({ ...item, value: e.target.value })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="">{__("Wszystkie")}</MenuItem>
                  {employmentFormOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
          ],
        },
        {
          headerName: __("Status aktywności"),
          field: "active",
          type: "singleSelect",
          width: 200,
          valueGetter: (_value, row) => row.active,
          valueFormatter: (value) => {
            if (value === true) return __("Aktywny");
            if (value === false) return __("Nieaktywny");
            return __("-");
          },
          filterOperators: [
            {
              label: __("równe"),
              value: "equals",
              getApplyFilterFn: (filterItem) => (params) => {
                if (filterItem.value === "") return true;
                if (filterItem.value === "true") return params.value === true;
                if (filterItem.value === "false") return params.value === false;
                return true;
              },
              InputComponent: ({
                item,
                applyValue,
              }: GridFilterInputValueProps) => (
                <Select
                  value={item.value || ""}
                  onChange={(e) =>
                    applyValue({ ...item, value: e.target.value })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                  displayEmpty
                >
                  <MenuItem value="">{__("Wszyscy")}</MenuItem>
                  <MenuItem value="true">{__("Aktywny")}</MenuItem>
                  <MenuItem value="false">{__("Nieaktywny")}</MenuItem>
                </Select>
              ),
            },
          ],
        },
        {
          field: "actions",
          type: "actions",
          headerName: __("Akcja"),
          pinnable: true,
          getActions: (params: GridRowParams) => [
            <GridLinkAction
              label={__("Nieobecności pracownika")}
              to={`${basePath}/absences/employee/${params.id}`}
              icon={<AccountCircleOutlinedIcon />}
              bgColor="primary.light"
              bgColorHover="primary.main"
              dataTestId="employee-link-action"
            />,
          ],
        },
      ] as GridColDef<IEmployeeRecord>[],
    [getStringOperators, employmentFormOptions, employmentFormLabels, basePath]
  );

  if (!initialState) {
    return null;
  }

  return (
    <Paper data-testid="absence-records-employees-table">
      <Box sx={{ height: 770, width: "100%" }}>
        {isError && (
          <Alert variant="outlined" color="warning">
            {String(error)}
          </Alert>
        )}
        <DataGrid
          apiRef={apiRef}
          columns={columns}
          rows={data?.content || []}
          getRowId={(row) => row.employeeId}
          rowCount={rowCount}
          loading={isFetching}
          initialState={initialState}
          pinnedColumns={{ left: [], right: ["actions"] }}
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          disableEval
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          density={settings.density}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableColumnFilter
          headerFilters={showFilters}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          filterDebounceMs={500}
          showToolbar
          slots={{
            headerFilterMenu: null,
            toolbar: () => (
              <CustomToolbar
                settings={settings}
                setSettings={setSettings}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
              />
            ),
            loadingOverlay: () => (
              <GridLoadingOverlay
                variant="linear-progress"
                noRowsVariant="skeleton"
              />
            ),
          }}
          slotProps={{
            toolbar: { printOptions: { disableToolbarButton: true } },
            row: {
              "data-testid": "absence-requests-table__row",
            },
            cell: {
              "data-testid": "absence-requests-table__cell",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default AbsenceRecordsEmployeesTable;
